import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { API, apiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import StatusTracker, { statusLabel } from "@/components/StatusTracker";
import StarRating from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Download, UploadCloud, Copy, Gift, Package, Star, Lock, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [active, setActive] = useState(null);
  const [referral, setReferral] = useState(null);
  const [testDl, setTestDl] = useState([]);

  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  const loadTest = () => api.get("/account/test-downloads").then((r) => setTestDl(r.data || [])).catch(() => setTestDl([]));
  useEffect(() => { load(); loadTest(); api.get("/referrals/me").then((r) => setReferral(r.data)); }, []);

  const downloadTest = async (t) => {
    // Mobile-safe: open a tab synchronously (avoids popup blockers), then point it at a
    // short-lived signed URL after the authenticated entitlement check. No Blob/anchor
    // download (unreliable on iOS Safari) and no private storage path exposed.
    const w = window.open("", "_blank");
    try {
      const { data } = await api.get(`/test-delivery/${t.id}/account-link`);
      const dl = `${API}${data.path}`;
      if (w) { w.location.href = dl; } else { window.location.href = dl; }
      toast.success("Opening your download…");
      loadTest();
    } catch (err) {
      if (w) w.close();
      toast.error(apiError(err) || "We couldn't open your download. Please try again.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slateink">Hi, {user?.name?.split(" ")[0] || "there"} 👋</h1>
          <p className="text-slate-500 mt-1">Track orders, download keepsakes and share the love.</p>
        </div>
        <Button asChild className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold hidden sm:inline-flex"><Link to="/shop">Create another</Link></Button>
      </div>

      {testDl.length > 0 && (
        <div className="mb-6 bg-blue-50/50 border border-blue-100 rounded-2xl p-5" data-testid="account-test-downloads">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <h2 className="font-semibold text-slateink">Digital downloads — test entitlements</h2>
          </div>
          <p className="text-xs text-slate-500 mt-1">These are internal test entitlements (no purchase, no charge). They can be revoked by an admin at any time.</p>
          <div className="grid gap-3 mt-4">
            {testDl.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 bg-white rounded-xl border border-slate-100 p-4" data-testid={`test-entitlement-${t.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slateink truncate">{t.product_name}</span>
                    <Badge className="bg-blue-100 text-blue-700 border-0">Test entitlement</Badge>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">Expires {new Date(t.expires_at).toLocaleString()}</p>
                </div>
                <Button size="sm" className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold shrink-0"
                  data-testid={`test-download-btn-${t.id}`} onClick={() => downloadTest(t)}>
                  <Download className="w-4 h-4 mr-1" /> Download
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="orders">
        <TabsList className="mb-6">
          <TabsTrigger value="orders" data-testid="tab-orders">My Orders</TabsTrigger>
          <TabsTrigger value="referrals" data-testid="tab-referrals">Refer &amp; Earn</TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          {orders === null ? (
            <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber" /></div>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto" />
              <p className="font-display text-xl font-bold text-slateink mt-4">No orders yet</p>
              <p className="text-slate-500 mt-1">Your keepsakes will appear here.</p>
              <Button asChild className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold mt-6"><Link to="/shop">Start creating</Link></Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {orders.map((o) => (
                <button key={o.id} onClick={() => setActive(o)} data-testid={`order-card-${o.order_number}`}
                  className="text-left bg-white rounded-2xl border border-slate-100 p-5 card-hover flex items-center gap-4">
                  <img src={o.items[0]?.image || "https://images.unsplash.com/photo-1616802099190-5a4485319df6?q=85&w=200"} alt="" className="w-16 h-16 rounded-xl object-cover bg-subtle" onError={(e) => (e.target.style.display = "none")} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slateink">{o.order_number}</span>
                      <Badge className={`border-0 text-white ${o.status === "refunded" || o.payment_status === "refunded" ? "bg-rose" : o.status === "delivered" || o.status === "ready" ? "bg-sage" : o.status === "pending_payment" ? "bg-rose" : o.status === "fulfillment_error" ? "bg-rose" : "bg-amber"}`}>{statusLabel(o.status)}</Badge>
                      {o.payment_status === "partially_refunded" && <Badge className="bg-amber-100 text-amber-700 border-0">Partial refund</Badge>}
                    </div>
                    <p className="text-sm text-slate-500 truncate mt-0.5">{o.items.map((i) => i.product_name).join(", ")}</p>
                    <p className="text-sm font-medium text-slateink mt-0.5">${o.total.toFixed(2)}</p>
                  </div>
                  {(o.status === "ready" || o.status === "delivered") && o.completed_file && <Download className="w-5 h-5 text-sage" />}
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="referrals">
          <ReferralCard referral={referral} />
        </TabsContent>
      </Tabs>

      {active && <OrderDialog order={active} onClose={() => setActive(null)} reload={load} />}
    </div>
  );
}

function ReferralCard({ referral }) {
  if (!referral) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber" /></div>;
  const copy = () => { navigator.clipboard.writeText(referral.referral_link); toast.success("Referral link copied!"); };
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-8 max-w-xl">
      <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber flex items-center justify-center mb-4"><Gift className="w-6 h-6" /></div>
      <h2 className="font-display text-2xl font-bold text-slateink">Give $5, Get $5</h2>
      <p className="text-slate-600 mt-2">Share your link. Your friend gets $5 off their first order, and you get $5 store credit after they buy.</p>
      <div className="flex gap-2 mt-5">
        <Input readOnly value={referral.referral_link} data-testid="referral-link" className="bg-subtle" />
        <Button onClick={copy} data-testid="copy-referral" className="rounded-lg bg-amber hover:bg-amber-600 text-white shrink-0"><Copy className="w-4 h-4" /></Button>
      </div>
      <div className="flex items-center gap-6 mt-6 pt-6 border-t border-slate-100">
        <div><p className="text-xs text-slate-400 uppercase font-semibold">Your code</p><p className="font-display text-lg font-bold text-slateink">{referral.referral_code}</p></div>
        <div><p className="text-xs text-slate-400 uppercase font-semibold">Store credit</p><p className="font-display text-lg font-bold text-sage">${(referral.store_credit || 0).toFixed(2)}</p></div>
      </div>
    </div>
  );
}

function OrderDialog({ order, onClose, reload }) {
  const [detail, setDetail] = useState(order);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [showInvoice, setShowInvoice] = useState(false);
  const inputRef = useRef();
  const canDownload = (detail.status === "ready" || detail.status === "delivered") && detail.completed_file;

  useEffect(() => {
    api.get(`/orders/${order.id}`).then((r) => {
      setDetail(r.data);
      track.orderTrackingViewed({ status: r.data.status, order_category: r.data.items?.[0]?.product_slug });
      if ((r.data.status === "ready" || r.data.status === "delivered") && r.data.completed_file) {
        track.digitalDownloadAvailable({ status: r.data.status });
      }
    });
  }, [order.id]);

  const upload = async () => {
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await api.post(`/orders/${order.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photos uploaded");
      setFiles([]);
      const { data } = await api.get(`/orders/${order.id}`);
      setDetail(data); reload();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const payNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };

  const download = async () => {
    try {
      const res = await api.get(`/orders/${order.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = detail.completed_file.filename; a.click();
      URL.revokeObjectURL(url);
      track.digitalDownloaded({ order_category: detail.items?.[0]?.product_slug });
    } catch (err) { toast.error(apiError(err)); }
  };

  const pz = detail.personalization || {};

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="order-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{detail.order_number}</DialogTitle></DialogHeader>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-slateink text-sm mb-3">Order progress</h4>
            {detail.status === "pending_payment" ? (
              <div className="bg-rose/5 border border-rose/20 rounded-xl p-4">
                <p className="text-sm text-slate-600">This order is awaiting payment.</p>
                <Button onClick={payNow} disabled={busy} data-testid="pay-now" className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold mt-3 w-full"><Lock className="w-4 h-4 mr-1" /> Complete payment</Button>
              </div>
            ) : (<>
              {detail.production?.status && !["ready"].includes(detail.production.status) && (
                <p className="text-xs text-amber-700/80 bg-amber-50 rounded-lg px-3 py-2 mb-3">
                  {({ generating: "Creating Your Book ✨", quality_review: "Quality Check in progress", needs_human_review: "We're completing a quality check on your order", rendering: "Almost ready…", waiting_for_photos: "Waiting for your photos" })[detail.production.status] || "In progress"}
                </p>
              )}
              <StatusTracker status={detail.status} />
            </>)}

            {canDownload && (
              <Button onClick={download} data-testid="download-completed" className="rounded-full bg-sage hover:bg-sage-600 text-white font-semibold mt-5 w-full"><Download className="w-4 h-4 mr-1" /> Download your book</Button>
            )}

            {detail.requires_photos && detail.status !== "pending_payment" && (
              <div className="mt-5">
                <h4 className="font-semibold text-slateink text-sm mb-2">Add more photos</h4>
                <button onClick={() => inputRef.current?.click()} className="w-full border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:border-amber transition-colors">
                  <UploadCloud className="w-6 h-6 text-amber mx-auto" /><p className="text-xs text-slate-500 mt-1">{files.length ? `${files.length} selected` : "Choose photos"}</p>
                </button>
                <input ref={inputRef} type="file" multiple accept="image/*,.heic,.heif" className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                {files.length > 0 && <Button onClick={upload} disabled={busy} className="w-full rounded-full mt-2 bg-amber hover:bg-amber-600 text-white text-sm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload"}</Button>}
              </div>
            )}
          </div>

          <div>
            <h4 className="font-semibold text-slateink text-sm mb-3">Your details</h4>
            <div className="bg-subtle rounded-xl p-4 text-sm space-y-1.5">
              {pz.recipient_name && <Row k="Recipient" v={pz.recipient_name} />}
              {pz.theme && <Row k="Theme" v={pz.theme} />}
              {pz.preferred_title && <Row k="Title" v={pz.preferred_title} />}
              {pz.favorite_colors && <Row k="Colors" v={pz.favorite_colors} />}
              {pz.special_message && <Row k="Message" v={pz.special_message} />}
            </div>

            {detail.assets?.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold text-slateink text-sm mb-2">Your photos ({detail.assets.length})</h4>
                <div className="grid grid-cols-4 gap-2">
                  {detail.assets.map((a) => (
                    <img key={a.id} src={`${API}/orders/${order.id}/assets/${a.id}`} alt="" className="w-full aspect-square object-cover rounded-lg bg-subtle" />
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowInvoice(true)} data-testid="view-invoice" className="text-xs"><FileText className="w-3.5 h-3.5 mr-1" /> Invoice</Button>
              <Button asChild variant="outline" size="sm" className="text-xs"><Link to={`/product/${detail.items[0]?.product_slug}`}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Reorder</Link></Button>
            </div>
          </div>
        </div>

        {(detail.status === "delivered" || detail.status === "ready") && !detail.review_id && (
          <ReviewForm order={detail} onDone={() => { toast.success("Thanks for your review!"); reload(); }} />
        )}

        {showInvoice && <InvoiceDialog order={detail} onClose={() => setShowInvoice(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) { return <div className="flex gap-2"><span className="text-slate-400 w-20 shrink-0">{k}</span><span className="text-slateink">{v}</span></div>; }

function ReviewForm({ order, onDone }) {
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [photoConsent, setPhotoConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reviews", { order_id: order.id, product_slug: order.items[0].product_slug, rating, title, body, photo_consent: photoConsent });
      onDone();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 pt-6 border-t border-slate-100">
      <h4 className="font-semibold text-slateink flex items-center gap-2"><Star className="w-4 h-4 text-amber" /> Leave a review</h4>
      <div className="flex gap-1 mt-3">
        {[1, 2, 3, 4, 5].map((i) => <button key={i} onClick={() => setRating(i)} data-testid={`review-star-${i}`}><Star className={`w-6 h-6 ${i <= rating ? "fill-amber text-amber" : "fill-slate-200 text-slate-200"}`} /></button>)}
      </div>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid="review-title" className="mt-3" />
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Tell us what you loved…" data-testid="review-body" className="mt-2" />
      <label className="flex items-center gap-2 mt-3 text-xs text-slate-600 cursor-pointer">
        <Checkbox checked={photoConsent} onCheckedChange={setPhotoConsent} /> Allow my review photos to be displayed publicly
      </label>
      <Button onClick={submit} disabled={busy} data-testid="submit-review" className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold mt-4">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit review"}</Button>
    </div>
  );
}

function InvoiceDialog({ order, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="invoice-dialog">
        <DialogHeader><DialogTitle className="font-display">Invoice — {order.order_number}</DialogTitle></DialogHeader>
        <div className="text-sm">
          <p className="text-slate-500">Billed to {order.customer_name} ({order.customer_email})</p>
          <div className="mt-4 space-y-2">
            {order.items.map((i, x) => (
              <div key={x} className="flex justify-between"><span className="text-slate-600">{i.product_name} — {i.variant.label}</span><span>${i.line_total.toFixed(2)}</span></div>
            ))}
            {order.discount_amount > 0 && <div className="flex justify-between text-sage"><span>Discount</span><span>−${order.discount_amount.toFixed(2)}</span></div>}
            {order.referral_discount > 0 && <div className="flex justify-between text-sage"><span>Referral</span><span>−${order.referral_discount.toFixed(2)}</span></div>}
            <div className="flex justify-between font-semibold text-base pt-2 border-t border-slate-100"><span>Total paid</span><span>${order.total.toFixed(2)}</span></div>
          </div>
          <p className="text-xs text-slate-400 mt-4">Made For Me Studio · Personalized digital gifts</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
